import {
  faGear,
  faDollarCircle,
  faLock,
} from '@fortawesome/pro-light-svg-icons';
import UserPlaceholder from '../../assets/icons/user-placeholder.png?component';
import {
  faBadgePercent,
  faBook,
  faChartLineUp,
  faEnvelope,
  faEnvelopeOpenDollar,
  faGraduationCap,
  faHome,
  faMoneyBillTransfer,
  faPhotoFilm,
  faRectangleAd,
  faSignalStream,
  faSquareList,
  faUser,
  faUsers,
  faUsersRectangle,
} from '@fortawesome/pro-regular-svg-icons';

import { PATHS } from '../../../routes';
import { externalLink, Role } from './helpers';
import { TagLabel } from '@client/new/pages/Marketing/Marketing.page';
import { NavMenu, NavMenuProps } from '@design-system/components/menu/NavMenu';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';
import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons';
import { externalUrls } from '@client/new/pages/creator/home/mappers';

export const CreatorContextMenu = ({
  influencer,
  role,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  influencer: any;
  role: Role;
}) => {
  const pathParams: { communityId?: number; influencerId: number } = {
    communityId: influencer.communityId,
    influencerId: influencer.id,
  };

  const flags: MenuItemsParams['flags'] = {
    canLiveStream: influencer.customizations
      ? !!influencer.customizations.canLiveStream
      : false,
    showSocial:
      isSubscriptionCreator(influencer.offering) ||
      hasPremiumFeaturesOTPIds(influencer?.customizations),
  };

  const menuList = getMenuItems({ flags, params: pathParams, role });
  const avatar = influencer.mediaCover
    ? `${influencer.mediaCover?.cdn01}${influencer.mediaCover?.image}`
    : UserPlaceholder;

  const displayName = `${influencer.firstName} ${influencer.lastName}`;

  return (
    <NavMenu
      list={menuList}
      onLogout={() => null}
      showProgress={false}
      isAdmin={true}
      displayName={displayName}
      avatar={avatar}
    />
  );
};

const commonMenuItems = ({ params }: MenuItemsParams) => [
  {
    section: 'Home',
    items: [
      {
        caption: 'Home',
        to: PATHS.HOME_CREATOR(params.influencerId),
        icon: faHome,
        testId: 'MainMenuItemHome',
      },
    ],
  },
  {
    section: 'Products',
    items: [
      {
        caption: 'One Time Purchases',
        to: PATHS.PURCHASES(params.influencerId),
        icon: faDollarCircle,
        testId: 'MainMenuItemOTP',
      },
      {
        caption: 'Subscription',
        to: PATHS.SUBSCRIPTIONS(params.influencerId),
        icon: faMoneyBillTransfer,
        testId: 'MainMenuItemSubscription',
      },
    ],
  },
  {
    section: 'Content',
    items: [
      {
        caption: 'Library',
        to: PATHS.LIBRARY(params.influencerId),
        icon: faPhotoFilm,
        testId: 'MainMenuItemLibrary',
      },
      {
        caption: 'Workouts',
        to: PATHS.PARTNER_WORKOUTS(params.influencerId),
        icon: faSquareList,
        subPaths: [
          PATHS.PARTNER_WORKOUTS_DRAFTS(params.influencerId),
          PATHS.PARTNER_WORKOUTS_SCHEDULED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_PUBLISHED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_ARCHIVED(params.influencerId),
        ],
        testId: 'MainMenuItemContent',
      },
    ],
  },
  {
    section: 'Education',
    items: [
      externalLink('Handbook', externalUrls['handbook'], faBook),
      externalLink('Growth Club', externalUrls['coaching'], faGraduationCap, {
        subPaths: [
          PATHS.PARTNER_WORKOUTS_DRAFTS(params.influencerId),
          PATHS.PARTNER_WORKOUTS_SCHEDULED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_PUBLISHED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_ARCHIVED(params.influencerId),
        ],
      }),
      externalLink(
        'Creator Community',
        externalUrls['community'],
        faPeopleGroup,
      ),
    ],
  },
];

const roleBasedSocialItems = ({ flags, params, role }: MenuItemsParams) => {
  if (flags.showSocial && (role === 'admin' || role === 'moderator')) {
    return [
      {
        section: 'Social',
        items: [
          {
            caption: 'Community',
            to: params.communityId
              ? PATHS.COMMUNITY(params.influencerId, params.communityId)
              : PATHS.COMMUNITY_INIT(params.influencerId),
            icon: faUsers,
            notificationCount: null,
            testId: 'MainMenuItemCommunity',
          },
          {
            caption: 'Direct Messages',
            to: PATHS.INBOX_MESSAGE(params.influencerId),
            icon: faEnvelope,
            testId: 'MainMenuItemDirectMessages',
          },
          ...(flags.canLiveStream
            ? [
                {
                  caption: 'Livestream',
                  to: PATHS.LIVESTREAM_VIEW(params.influencerId),
                  icon: faSignalStream,
                  testId: 'MainMenuItemLivestream',
                },
              ]
            : []),
        ],
      },
    ];
  }
  return [];
};

const roleBasedToolsItems = ({ params, role }: MenuItemsParams) => {
  if (role === 'admin' || role === 'moderator') {
    return [
      {
        section: 'Tools',
        items: [
          {
            caption: 'Audience',
            to: PATHS.CRM_CREATOR(params.influencerId, 'users'),
            icon: faUsersRectangle,
            testId: 'MainMenuItemAudience',
          },
          {
            caption: 'Analytics',
            to: PATHS.PARTNER_ANALYTICS(params.influencerId),
            icon: faChartLineUp,
            testId: 'MainMenuItemAnalytics',
          },
          {
            caption: 'Payouts',
            to: PATHS.ADMIN_PAYMENT(params.influencerId),
            icon: faEnvelopeOpenDollar,
            testId: 'MainMenuItemPayouts',
          },
          {
            caption: (
              <span className="relative">
                Marketing
                <TagLabel small variant="primary">
                  beta
                </TagLabel>
              </span>
            ),
            to: PATHS.MARKETING(params.influencerId),
            icon: faRectangleAd,
            testId: 'MainMenuItemMarketing',
          },
          {
            caption: (
              <span className="relative" data-testid="menu-item-promotions">
                Promotions
                <TagLabel variant="secondary" small>
                  NEW
                </TagLabel>
              </span>
            ),
            to: PATHS.PROMOTIONS(params.influencerId),
            icon: faBadgePercent,
            testId: 'MainMenuItemPromotions',
          },
        ],
      },
    ];
  }
  return [];
};

const roleBasedAccountItems = ({ params, role }: MenuItemsParams) => {
  if (role === 'admin' || role === 'moderator') {
    return [
      {
        section: 'Account',
        items: [
          {
            caption: 'Profile',
            to: PATHS.PARTNER_EDIT(params.influencerId),
            icon: faUser,
            testId: 'MainMenuItemProfile',
          },
          {
            caption: 'Settings',
            to: PATHS.PARTNER_SETTINGS(params.influencerId),
            icon: faGear,
            testId: 'MainMenuItemSettings',
          },
          {
            caption: 'Administration',
            to: PATHS.ADMIN_ADMINISTRATION(params.influencerId),
            icon: faLock,
            testId: 'MainMenuItemAdministration',
          },
        ],
      },
    ];
  }
  if (role === 'contentEditor') {
    return [
      {
        section: 'Account',
        items: [
          {
            caption: 'Profile',
            to: PATHS.PARTNER_EDIT(params.influencerId),
            icon: faUser,
            testId: 'MainMenuItemProfile',
          },
        ],
      },
    ];
  }
  return [];
};

export type MenuItemsParams = {
  flags: Partial<{
    canLiveStream: boolean;
    canAdTracking: boolean;
    showSocial: boolean;
  }>;
  params: { influencerId: number; communityId?: number };
  role: Role;
};

export const getMenuItems = (p: MenuItemsParams): NavMenuProps['list'] => [
  ...commonMenuItems(p),
  ...roleBasedSocialItems(p),
  ...roleBasedToolsItems(p),
  ...roleBasedAccountItems(p),
];
